// IndexedDB: signing key, account registry, fill ledger, brackets and settings.

// NOT renamed with the project: an IndexedDB DB name change orphans existing data instead of migrating it.
const DB_NAME = 'kalshigui2';
const DB_VERSION = 2;

export const STORE_CREDS = 'creds';
export const STORE_META = 'meta';
export const STORE_FILLS = 'fills';
/** Order journal (see journal.ts): auto-incremented key, entries as written. */
export const STORE_JOURNAL = 'journal';

export interface FillRow {
  fill_id: string;
  ts: number;            // epoch seconds
  ticker: string;
  side: 'yes' | 'no';    // buy-normalised outcome side (net-YES sign)
  mq: number;            // milli-contracts
  yes_price_mc: number;
  fee_cash: number;
  is_taker: boolean;
  order_id: string;
  client_order_id: string;
  account_id: string;
}

let dbPromise: Promise<IDBDatabase> | null = null;

export function openDb(): Promise<IDBDatabase> {
  if (dbPromise) return dbPromise;
  dbPromise = new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE_CREDS)) db.createObjectStore(STORE_CREDS);
      if (!db.objectStoreNames.contains(STORE_META)) db.createObjectStore(STORE_META);
      if (!db.objectStoreNames.contains(STORE_FILLS)) {
        const fills = db.createObjectStore(STORE_FILLS, { keyPath: 'fill_id' });
        fills.createIndex('account_id', 'account_id', { unique: false });
        fills.createIndex('ticker', 'ticker', { unique: false });
        fills.createIndex('ts', 'ts', { unique: false });
      }
      if (!db.objectStoreNames.contains(STORE_JOURNAL)) db.createObjectStore(STORE_JOURNAL, { autoIncrement: true });
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
  return dbPromise;
}

function wrap<T>(req: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

export async function idbGet<T>(store: string, key: IDBValidKey): Promise<T | undefined> {
  const db = await openDb();
  return wrap<T>(db.transaction(store, 'readonly').objectStore(store).get(key) as IDBRequest<T>);
}

export async function idbPut(store: string, value: unknown, key?: IDBValidKey): Promise<void> {
  const db = await openDb();
  const tx = db.transaction(store, 'readwrite');
  tx.objectStore(store).put(value as any, key);
  await txDone(tx);
}

export async function idbDelete(store: string, key: IDBValidKey): Promise<void> {
  const db = await openDb();
  const tx = db.transaction(store, 'readwrite');
  tx.objectStore(store).delete(key);
  await txDone(tx);
}

export async function idbGetAll<T>(store: string): Promise<T[]> {
  const db = await openDb();
  return wrap<T[]>(db.transaction(store, 'readonly').objectStore(store).getAll() as IDBRequest<T[]>);
}

export async function idbClear(store: string): Promise<void> {
  const db = await openDb();
  const tx = db.transaction(store, 'readwrite');
  tx.objectStore(store).clear();
  await txDone(tx);
}

function txDone(tx: IDBTransaction): Promise<void> {
  return new Promise((resolve, reject) => {
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error ?? new Error('indexeddb transaction failed')); // tx.error can be null
    tx.onabort = () => reject(tx.error ?? new Error('indexeddb transaction aborted'));
  });
}

/** Append a fill, deduped by `fill_id` (a WS reconnect replays recent fills). Returns false if already there. */
export async function appendFill(fill: FillRow): Promise<boolean> {
  return (await appendFills([fill])) === 1;
}

/** Insert fills, skipping ones already present via `add()`'s ConstraintError on a duplicate key. */
export async function appendFills(fills: FillRow[]): Promise<number> {
  if (!fills.length) return 0;
  const db = await openDb();
  const tx = db.transaction(STORE_FILLS, 'readwrite');
  const store = tx.objectStore(STORE_FILLS);
  let added = 0;
  const results = fills.map((f) => new Promise<void>((resolve) => {
    const req = store.add(f);
    req.onsuccess = () => { added++; resolve(); };
    req.onerror = (ev) => { ev.preventDefault(); resolve(); }; // duplicate fill_id
  }));
  // NOT txDone: a duplicate's error event still bubbles to tx.onerror even after preventDefault().
  await new Promise<void>((resolve, reject) => {
    tx.oncomplete = () => resolve();
    tx.onabort = () => reject(tx.error ?? new Error('fill write aborted'));
  });
  await Promise.all(results);
  return added;
}

export async function allFills(): Promise<FillRow[]> {
  const rows = await idbGetAll<FillRow>(STORE_FILLS);
  rows.sort((a, b) => (a.ts || 0) - (b.ts || 0));
  return rows;
}

export async function getMeta<T>(key: string, fallback: T): Promise<T> {
  const v = await idbGet<T>(STORE_META, key);
  return v === undefined ? fallback : v;
}

export async function setMeta(key: string, value: unknown): Promise<void> {
  await idbPut(STORE_META, value, key);
}

// Manual fill -> account reassignments: the only attribution that can't be derived from Kalshi, so it's stored.
const OVERRIDES_KEY = 'fill_account_overrides_v1';

export type FillOverrides = Record<string, string>;

export async function getFillOverrides(): Promise<FillOverrides> {
  return getMeta<FillOverrides>(OVERRIDES_KEY, {});
}

export async function setFillOverrides(map: FillOverrides): Promise<void> {
  await setMeta(OVERRIDES_KEY, map);
}

/** Repoint one stored fill at another account. False if the fill is unknown. */
export async function updateFillAccount(fillId: string, accountId: string): Promise<boolean> {
  const db = await openDb();
  const tx = db.transaction(STORE_FILLS, 'readwrite');
  const store = tx.objectStore(STORE_FILLS);
  const found = await new Promise<boolean>((resolve, reject) => {
    const req = store.get(fillId);
    req.onsuccess = () => {
      const row = req.result as FillRow | undefined;
      if (!row) { resolve(false); return; }
      row.account_id = accountId;
      store.put(row);
      resolve(true);
    };
    req.onerror = () => reject(req.error);
  });
  // Wait for the COMMIT, not just the put request, so a ledger replay right after doesn't race this write.
  await txDone(tx);
  return found;
}

export async function journalAppend(entry: unknown): Promise<void> {
  const db = await openDb();
  await wrap(db.transaction(STORE_JOURNAL, 'readwrite').objectStore(STORE_JOURNAL).add(entry));
}

export async function journalAll<T = unknown>(): Promise<T[]> {
  const db = await openDb();
  return wrap(db.transaction(STORE_JOURNAL, 'readonly').objectStore(STORE_JOURNAL).getAll()) as Promise<T[]>;
}

export async function journalClear(): Promise<void> {
  const db = await openDb();
  await wrap(db.transaction(STORE_JOURNAL, 'readwrite').objectStore(STORE_JOURNAL).clear());
}

/** Drop the oldest entries so at most `keep` remain. */
export async function journalPrune(keep: number): Promise<void> {
  const db = await openDb();
  const store = db.transaction(STORE_JOURNAL, 'readwrite').objectStore(STORE_JOURNAL);
  const n = await wrap(store.count());
  if (n <= keep) return;
  const keys = await wrap(store.getAllKeys(undefined, n - keep));
  const last = keys[keys.length - 1];
  if (last !== undefined) await wrap(store.delete(IDBKeyRange.upperBound(last)));
}
